import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const translations = {
  en: {
    nav: {
      home: 'Home',
      careerStreams: 'Career Streams',
      after10th: 'After 10th',
      after12th: 'After 12th',
      examCalendar: 'Exam Calendar',
      freeCounseling: 'Free Counseling',
      bookFreeCounseling: 'Book Free Counseling',
    },
    language: {
      label: 'Language',
      english: 'English',
      hindi: 'Hindi',
    },
    common: {
      backToStreams: 'Back to Streams',
      backToCareers: 'Back to Careers',
      home: 'Home',
      after10th: 'After 10th',
      after12th: 'After 12th',
      duration: 'Duration',
      averageFees: 'Average Fees',
      fees: 'Fees',
      eligibility: 'Eligibility',
      leadsTo: 'Leads To',
      aboutThisPath: 'About This Path',
      whyChoose: 'Why Choose',
      subjectsOptions: 'Subjects / Options',
      availableTrades: 'Available Trades',
      diplomaBranches: 'Diploma Branches',
      keySubjects: 'Key Subjects',
      sampleCourses: 'Sample Courses',
      careerPaths: 'Career Paths',
      nextStep: 'Next Step',
      quickTip: 'Quick Tip',
      previous: 'Previous',
      next: 'Next Step',
      stepByStepRoadmap: 'Step-by-Step Roadmap',
      allStepsOverview: 'Complete Roadmap Overview',
      careerGrowthSalary: 'Career Growth & Salary',
      studentsMustKnow: 'Students Must Know',
      keyThings: 'Key Things to Remember',
      programmingLanguagesToKnow: 'Programming Languages to Know',
      topRecruiters: 'Top Recruiters',
      entranceExams: 'Entrance Exams',
      topColleges: 'Top Colleges',
      showLess: 'Show less',
      more: 'more',
      needGuidance: 'Need Guidance?',
      needPersonalizedGuidance: 'Need Personalized Guidance?',
      bookFreeCounseling: 'Book Free Counseling',
      after12thCareers: 'After 12th Careers',
      complete12thToExplore: 'Complete 12th in this stream to explore detailed career roadmaps ↓',
      backToAllPathways: 'Back to All Pathways',
      backToAfter10th: 'Back to After 10th',
      backToAfter12th: 'Back to After 12th',
      backToCareersList: 'Back to careers',
      levelChosen: 'Level Chosen',
      chooseStream: 'Choose Stream',
      notSure: 'Not sure',
      step2ChooseStream: 'Step 2: Choose Stream',
      step3ChooseCareer: 'Step 3: Choose Career',
      stream: 'Stream',
      criteria: 'Criteria',
      bestFor: 'Best For',
      topCareer: 'Top Career',
      exploreAll: 'Explore All',
      view: 'View',
      open: 'Open',
      openPage: 'Open page',
      explore: 'Explore',
      goal: 'Goal',
      viewDetails: 'View Details',
      hot: 'Hot',
      streamSwitchingPossible: 'Stream switching is possible!',
      streamSwitchingBody: 'Science students can also choose Commerce/Arts careers. Hotel Management, Law, Design, and many courses accept students from ALL streams.',
      completeRoadmapOverview: 'Complete Roadmap Overview',
      roadmapLegend: 'Roadmap Legend',
      stepTypesLabel: 'Step Types',
      moreCareersComing: 'More Careers Coming',
      exploreCareerStreams: 'Explore Career Streams',
      topEntranceExams: 'Top Entrance Exams',
      clickToSeeAll: 'Click to see all',
      careerGuidance: 'Career Guidance',
      streamComparison: 'Stream Comparison',
      changeStream: 'Change Stream',
      step1of3: 'Step 1 of 3',
      chooseYourLevel: 'Choose Your Level',
      whereAreYouRightNow: 'Where Are You Right Now?',
      completed10thGrade: 'Completed 10th Grade',
      completed12thGrade: 'Completed 12th Grade',
      selectEducationLevel: 'Select your education level to get a personalised career roadmap',
      exploreMyOptions: 'Explore my options',
      browseExamCalendar: 'You can also browse the Exam Calendar or explore careers directly',
      browseExamCalendarPrefix: 'You can also browse the',
      browseExamCalendarSuffix: 'or explore careers directly',
      scienceStream: 'Science Stream',
      commerceStream: 'Commerce Stream',
      artsStream: 'Arts / Humanities',
      proTip: 'Pro Tip',
      indiaSalary: 'India Salary',
      globalSalary: 'Global Salary',
      step: 'Step',
      steps: 'steps',
      of: 'of',
      labelsHelp: 'Labels help you understand the institute type: IIT/NIT/IIIT for engineering, AIIMS for medical, NLU for law, and Govt/State or Private for the rest.',
      labelsHelpCollege: 'Labels help students quickly spot institute type, reputation, and the path they usually serve.',
      labelsHelpCompare: 'College labels help you read the institute type quickly before comparing placements and fees.',
      guidancePrompt: 'Need 1-on-1 Guidance?',
      guidanceBodyPrefix: 'Book a free session with a',
      guidanceBodySuffix: 'career expert.',
      importantNote: 'Important Note',
      keyFacts: 'Key Facts',
      stepTypes: {
        education: 'Education',
        exam: 'Entrance Exam',
        training: 'Training',
        milestone: 'Milestone',
        growth: 'Career Growth',
      },
    },
    footer: {
      description: 'A modern career guidance platform for Indian students after 10th and 12th grade, refreshed for April 2026.',
      quickLinks: 'Quick Links',
      careerStreams: 'Career Streams',
      keyExams: 'Key Exams 2026',
      copyright: '© 2026 CareerGuide India. All information is for guidance purposes only.',
      madeFor: 'Made for Indian Students',
      updated: 'Updated: Jul 2026',
      streams: {
        engineering: 'Engineering & Technology',
        medical: 'Medical & Healthcare',
        pharmacy: 'Pharmacy',
        commerce: 'Commerce & Finance',
        arts: 'Arts & Law',
        design: 'Design & Animation',
      },
      exams: ['JEE Main & Advanced', 'NEET-UG', 'CLAT', 'CA Foundation', 'BITSAT', 'NIFT', 'NCHMCT JEE'],
    },
    streamTitles: {
      engineering: 'Engineering & Technology',
      medical: 'Medical & Healthcare',
      pharmacy: 'Pharmacy',
      commerce: 'Commerce & Finance',
      arts: 'Arts & Law',
      design: 'Design & Animation',
    },
    streamSummaries: {
      engineering: 'Maths, coding, machines, electronics, and future tech careers.',
      medical: 'Patient care, biology, healthcare, and medical professions.',
      pharmacy: 'Medicines, drug science, and healthcare industry roles.',
      commerce: 'Accounts, finance, business, CA, and management careers.',
      arts: 'Law, UPSC, communication, policy, and creative careers.',
      design: 'Visual creativity, animation, UI/UX, and product design.',
    },
    streamHub: {
      badge: 'Career Streams',
      title: 'Choose a stream, then choose your path',
      subtitle: 'Open any stream to see what it leads to, what to study, which exams matter, and how students usually compare colleges and career routes.',
      cardLink: 'Open page',
    },
    streamGuide: {
      back: 'Back to Career Streams',
      about: 'About This Stream',
      aboutSub: 'A quick explanation of what this stream is best for and who it suits.',
      study: 'What You Study',
      studySub: 'The main subjects and skills students build in 11th and 12th.',
      careers: 'Career Directions',
      careersSub: 'The main routes students usually move into after 12th or graduation.',
      exams: 'Entrance Exams',
      examsSub: 'The major exams students often prepare for in this stream.',
      colleges: 'Top Colleges & Labels',
      collegesSub: 'Labels help you compare institutes faster and understand the type of campus.',
      programming: 'Programming Languages to Know',
      programmingSub: 'Useful for tech-heavy careers and future skill building.',
      tools: 'Tools, Software or Skills',
      toolsSub: 'Early skills that make projects and portfolios stronger.',
      quickDirection: 'Quick Direction',
      keyFacts: 'Key Facts',
      nextLinks: 'Next Links',
      openRoadmaps: 'Open stream-specific roadmaps',
      examCalendar: 'Check exam calendar',
      counseling: 'Book free counseling',
      notFound: 'Stream page not found',
    },
    hero: {
      badge: 'India’s career guidance hub, updated April 2026',
      titleLine1: 'Your next career decision',
      titleLine2: 'starts here',
      description: 'CareerGuide helps you move from confusion to clarity. Explore step-by-step roadmaps for Engineering, Medical, Commerce, Pharmacy, Law, Design, and skill-based routes after 10th or 12th grade.',
      point1: 'Pick your class and stream in seconds',
      point2: 'Compare exams, colleges, and salary paths side by side',
      point3: 'Follow a roadmap that tells you what to do next',
      after10th: 'After 10th',
      after12th: 'After 12th',
      exams: 'Exam Calendar',
      scroll: 'Scroll to explore',
      stats: {
        roadmaps: 'Career roadmaps',
        exams: 'Major exams tracked',
        colleges: 'Top colleges',
        latest: 'Latest refresh',
      },
    },
    home: {
      ctaBadge: 'Personalised Career Roadmap',
      ctaTitle: 'Get Your Personalized Career Roadmap',
      ctaDescription: 'Answer a few simple questions and get a practical roadmap that shows the right stream, the main entrance exams, college options, and the salary direction for that career path.',
      after10thTitle: 'After 10th Grade',
      after10thSub: '6 pathways available',
      after12thTitle: 'After 12th Grade',
      after12thSub: '25+ career roadmaps',
      noSignup: 'No signup required · 100% Free · Updated Apr 2026',
      howItWorks: 'How It Works',
      step1Title: 'Select Your Level',
      step1Desc: "Tell us if you've completed 10th or 12th grade",
      step2Title: 'Choose Your Stream',
      step2Desc: 'Science (PCM/PCB), Commerce, Arts, ITI, Polytechnic',
      step3Title: 'Get Your Roadmap',
      step3Desc: 'Step-by-step guide with exams, colleges, salary & timeline',
      possibilityBadge: 'Your future options',
      possibilityTitle: 'What can this lead to?',
      possibilityDesc: 'One stream can open several directions. You may move into a degree, a government exam, an industry job, freelancing, research, or even a startup route.',
      possibilityOneTitle: 'Professional degrees',
      possibilityOneDesc: 'B.Tech, MBBS, B.Com, BBA, B.Pharm, B.Des, LLB and more.',
      possibilityTwoTitle: 'Competitive exams',
      possibilityTwoDesc: 'JEE, NEET, CLAT, NIFT, CA, UPSC, banking and state-level entrances.',
      possibilityThreeTitle: 'Fast career entry',
      possibilityThreeDesc: 'ITI, Polytechnic and skill paths can lead to jobs in 6-24 months.',
      possibilityFourTitle: 'Growth and flexibility',
      possibilityFourDesc: 'Switch, specialize, freelance, study abroad, or build your own business.',
      exampleTitle: 'Example Career Roadmaps',
      exampleDesc: 'See how students move from 10th or 12th into a career with a clear sequence of subjects, exams, degrees, and job steps.',
      popularTitle: 'Popular Career Options',
      popularDesc: 'Open any career card to see what to study, which exam to target, and how the path typically grows over time.',
      upcomingTitle: 'Upcoming Exams 2026',
      upcomingDesc: 'Stay ahead of important entrance dates, application windows, and current exam status updates.',
      salaryTitle: 'Salary Snapshot 2026',
      salaryDesc: 'A simple view of where the earning potential usually starts and where it can grow after experience.',
      counselingTitle: 'Still Confused? Talk to an Expert!',
      counselingDesc: 'If you’re stuck between streams or careers, book a free 1-on-1 session. We’ll help you think through marks, interests, budget, and realistic next steps.',
      startQuiz: 'Start Free Career Quiz',
      bookCounseling: 'Book Free Counseling',
      viewAll: 'View All',
    },
    after10thPage: {
      badge: 'After 10th Grade',
      title: 'Choose Your Path After 10th',
      subtitle: '6 clear pathways — each leading to a different set of careers. Click any path to explore details, subjects, salary & opportunities.',
      tipTitle: 'Pro Tip for 10th Students',
      tipBody: 'The path you choose now shapes the next 20 years. Choose based on your interest + aptitude, not peer pressure. All 6 paths have excellent career opportunities. Click each card to see the complete picture.',
      compareTitle: 'Stream Comparison',
      compareSubtitle: 'Which 11th-12th stream is right for you?',
      ctaPrompt: 'Done with 12th? Get detailed career roadmaps',
      ctaButton: 'Explore After 12th Careers →',
    },
    after12thPage: {
      badge: 'After 12th Grade',
      title: 'Choose Your Career Stream',
      subtitle: 'Select your 12th stream below to see all career paths with step-by-step roadmaps, top colleges, entrance exams & salary data.',
      switchTitle: 'Good News — Stream Switching is Possible!',
      switchBody: 'Hotel Management, Law (CLAT), Design (NIFT/NID), Civil Services (UPSC), and many other courses accept students from ALL streams. Science students can always switch to Commerce/Arts careers.',
      sectionTitle: 'Jump to a Career Directly',
    },
    examCalendar: {
      badge: '📅 April 2026 Exam Schedule',
      title: 'Entrance Exam Calendar',
      subtitle: 'Stay updated with all major entrance exams for Engineering, Medical, Commerce, Pharmacy, Law, and Design.',
      searchPlaceholder: 'Search exam name or stream...',
      filterAll: 'All',
      filterEngineering: 'Engineering',
      filterMedical: 'Medical',
      filterCommerce: 'Commerce',
      filterPharmacy: 'Pharmacy',
      filterLaw: 'Law',
      filterDesign: 'Design',
      filterHotel: 'Hotel Management',
      statusActive: 'Active',
      statusUpcoming: 'Upcoming',
      statusCompleted: 'Completed',
      noExams: 'No exams found. Try a different search or filter.',
      importantTitle: 'Important Note',
      importantBody: 'Exam dates may change. Always verify on official websites. Register early and keep all documents ready.',
      prepTitle: 'Preparation Tips',
      prepBody: 'Start early, practice previous year papers, and follow the official syllabus and updates.',
    },
    counselingModal: {
      title: 'Book Free Career Counseling',
      subtitle: '1-on-1 session with a career expert. 100% Free.',
      stepDetails: 'Your Details',
      stepSubject: 'Career Interest',
      stepTime: 'Pick a Time',
      fullName: 'Full Name *',
      fullNamePlaceholder: 'Enter your full name',
      phone: 'Phone Number *',
      phonePlaceholder: '10-digit mobile number',
      email: 'Email (optional)',
      emailPlaceholder: 'your@email.com',
      currentClass: 'Current Class *',
      careerInterest: 'Which career are you interested in? *',
      question: 'Any specific question or concern?',
      questionPlaceholder: "e.g. I'm confused between Engineering and Medical. My NEET score is 550. What should I do?",
      preferredDate: 'Preferred Date *',
      preferredTime: 'Preferred Time *',
      bookingSummary: 'Booking Summary',
      whatToExpect: 'What to expect:',
      successTitle: 'Booking Confirmed!',
      successBodyPrefix: 'Hi',
      successBodyMid: 'Your counseling session has been booked for',
      successBodyEnd: 'A confirmation will be sent to',
      done: 'Done 🎉',
      next: 'Next →',
      back: '← Back',
      confirm: 'Confirm Booking ✓',
      step1Label: 'Your Details',
      step2Label: 'Choose Subject',
      step3Label: 'Pick a Time',
    },
  },
  hi: {
    nav: {
      home: 'होम',
      careerStreams: 'करियर स्ट्रीम',
      after10th: '10वीं के बाद',
      after12th: '12वीं के बाद',
      examCalendar: 'परीक्षा कैलेंडर',
      freeCounseling: 'निःशुल्क काउंसलिंग',
      bookFreeCounseling: 'निःशुल्क काउंसलिंग बुक करें',
    },
    language: {
      label: 'भाषा',
      english: 'English',
      hindi: 'हिंदी',
    },
    common: {
      backToStreams: 'स्ट्रीम पर वापस',
      backToCareers: 'करियर पर वापस',
      home: 'होम',
      after10th: '10वीं के बाद',
      after12th: '12वीं के बाद',
      duration: 'अवधि',
      averageFees: 'औसत फीस',
      fees: 'फीस',
      eligibility: 'योग्यता',
      leadsTo: 'ले जाता है',
      aboutThisPath: 'इस रास्ते के बारे में',
      whyChoose: 'क्यों चुनें',
      subjectsOptions: 'विषय / विकल्प',
      availableTrades: 'उपलब्ध ट्रेड',
      diplomaBranches: 'डिप्लोमा ब्रांच',
      keySubjects: 'मुख्य विषय',
      sampleCourses: 'नमूना कोर्स',
      careerPaths: 'करियर पथ',
      nextStep: 'अगला कदम',
      quickTip: 'त्वरित सुझाव',
      previous: 'पिछला',
      next: 'अगला कदम',
      stepByStepRoadmap: 'स्टेप-बाय-स्टेप रोडमैप',
      allStepsOverview: 'पूरा रोडमैप ओवरव्यू',
      careerGrowthSalary: 'करियर ग्रोथ और सैलरी',
      studentsMustKnow: 'छात्रों को यह जानना चाहिए',
      keyThings: 'याद रखने योग्य मुख्य बातें',
      programmingLanguagesToKnow: 'जानने योग्य प्रोग्रामिंग लैंग्वेज',
      topRecruiters: 'शीर्ष नियोक्ता',
      entranceExams: 'एंट्रेंस परीक्षाएँ',
      topColleges: 'शीर्ष कॉलेज',
      showLess: 'कम दिखाएँ',
      more: 'और',
      needGuidance: 'मार्गदर्शन चाहिए?',
      needPersonalizedGuidance: 'व्यक्तिगत मार्गदर्शन चाहिए?',
      bookFreeCounseling: 'निःशुल्क काउंसलिंग बुक करें',
      after12thCareers: '12वीं के बाद के करियर',
      complete12thToExplore: 'विस्तृत करियर रोडमैप देखने के लिए इस stream में 12वीं पूरी करें ↓',
      backToAllPathways: 'सभी रास्तों पर वापस',
      backToAfter10th: '10वीं के बाद पर वापस',
      backToAfter12th: '12वीं के बाद पर वापस',
      backToCareersList: 'करियर सूची पर वापस',
      levelChosen: 'चुना गया स्तर',
      chooseStream: 'स्ट्रीम चुनें',
      notSure: 'कन्फ्यूज़ हैं',
      step2ChooseStream: 'स्टेप 2: स्ट्रीम चुनें',
      step3ChooseCareer: 'स्टेप 3: करियर चुनें',
      stream: 'स्ट्रीम',
      criteria: 'मापदंड',
      bestFor: 'किसके लिए बेहतर',
      topCareer: 'शीर्ष करियर',
      exploreAll: 'सभी देखें',
      view: 'देखें',
      open: 'खोलें',
      openPage: 'पेज खोलें',
      explore: 'एक्सप्लोर',
      goal: 'लक्ष्य',
      viewDetails: 'विवरण देखें',
      hot: 'हॉट',
      streamSwitchingPossible: 'स्ट्रीम बदलना संभव है!',
      streamSwitchingBody: 'Science के छात्र Commerce/Arts careers भी चुन सकते हैं. Hotel Management, Law, Design और कई courses सभी streams के छात्रों को स्वीकार करते हैं.',
      completeRoadmapOverview: 'पूरा रोडमैप ओवरव्यू',
      roadmapLegend: 'रोडमैप लेजेंड',
      stepTypesLabel: 'स्टेप प्रकार',
      moreCareersComing: 'और करियर जल्द आ रहे हैं',
      exploreCareerStreams: 'करियर स्ट्रीम देखें',
      topEntranceExams: 'शीर्ष प्रवेश परीक्षाएँ',
      clickToSeeAll: 'सभी देखने के लिए क्लिक करें',
      careerGuidance: 'करियर मार्गदर्शन',
      streamComparison: 'स्ट्रीम तुलना',
      changeStream: 'स्ट्रीम बदलें',
      step1of3: '3 में से स्टेप 1',
      chooseYourLevel: 'अपना स्तर चुनें',
      whereAreYouRightNow: 'आप अभी कहाँ हैं?',
      completed10thGrade: '10वीं पूरी की',
      completed12thGrade: '12वीं पूरी की',
      selectEducationLevel: 'व्यक्तिगत करियर रोडमैप पाने के लिए अपना education level चुनें',
      exploreMyOptions: 'मेरे विकल्प देखें',
      browseExamCalendar: 'आप परीक्षा कैलेंडर भी देख सकते हैं या सीधे करियर explore कर सकते हैं',
      browseExamCalendarPrefix: 'आप',
      browseExamCalendarSuffix: 'भी देख सकते हैं या सीधे करियर explore कर सकते हैं',
      scienceStream: 'साइंस स्ट्रीम',
      commerceStream: 'कॉमर्स स्ट्रीम',
      artsStream: 'आर्ट्स / ह्यूमैनिटीज़',
      proTip: 'प्रो टिप',
      indiaSalary: 'भारत सैलरी',
      globalSalary: 'वैश्विक सैलरी',
      step: 'स्टेप',
      steps: 'स्टेप्स',
      of: 'में से',
      labelsHelp: 'लेबल आपको institute type समझने में मदद करते हैं: engineering के लिए IIT/NIT/IIIT, medical के लिए AIIMS, law के लिए NLU, और बाकी के लिए Govt/State या Private.',
      labelsHelpCollege: 'लेबल से छात्र institute type, reputation और सामान्य path को जल्दी पहचान सकते हैं.',
      labelsHelpCompare: 'कॉलेज लेबल से placement और fees compare करने से पहले institute type जल्दी समझ आता है.',
      guidancePrompt: '1-on-1 मार्गदर्शन चाहिए?',
      guidanceBodyPrefix: 'एक',
      guidanceBodySuffix: 'career expert के साथ मुफ्त session बुक करें.',
      importantNote: 'महत्वपूर्ण नोट',
      keyFacts: 'मुख्य तथ्य',
      stepTypes: {
        education: 'शिक्षा',
        exam: 'प्रवेश परीक्षा',
        training: 'प्रशिक्षण',
        milestone: 'मील का पत्थर',
        growth: 'करियर ग्रोथ',
      },
    },
    footer: {
      description: '10वीं और 12वीं के बाद भारतीय छात्रों के लिए एक आधुनिक करियर गाइड प्लेटफ़ॉर्म, अप्रैल 2026 के अनुसार अपडेटेड.',
      quickLinks: 'त्वरित लिंक',
      careerStreams: 'करियर स्ट्रीम',
      keyExams: 'मुख्य परीक्षाएँ 2026',
      copyright: '© 2026 CareerGuide India. सभी जानकारी केवल मार्गदर्शन के लिए है.',
      madeFor: 'भारतीय छात्रों के लिए',
      updated: 'अपडेटेड: जुलाई 2026',
      streams: {
        engineering: 'इंजीनियरिंग और टेक्नोलॉजी',
        medical: 'मेडिकल और हेल्थकेयर',
        pharmacy: 'फार्मेसी',
        commerce: 'कॉमर्स और फाइनेंस',
        arts: 'आर्ट्स और लॉ',
        design: 'डिज़ाइन और एनीमेशन',
      },
      exams: ['JEE Main & Advanced', 'NEET-UG', 'CLAT', 'CA Foundation', 'BITSAT', 'NIFT', 'NCHMCT JEE'],
    },
    streamTitles: {
      engineering: 'इंजीनियरिंग और टेक्नोलॉजी',
      medical: 'मेडिकल और हेल्थकेयर',
      pharmacy: 'फार्मेसी',
      commerce: 'कॉमर्स और फाइनेंस',
      arts: 'आर्ट्स और लॉ',
      design: 'डिज़ाइन और एनीमेशन',
    },
    streamSummaries: {
      engineering: 'मैथ्स, कोडिंग, मशीनें, इलेक्ट्रॉनिक्स और भविष्य की टेक करियर.',
      medical: 'पेशेंट केयर, बायोलॉजी, हेल्थकेयर और मेडिकल प्रोफेशन.',
      pharmacy: 'दवाइयाँ, ड्रग साइंस और हेल्थकेयर इंडस्ट्री भूमिकाएँ.',
      commerce: 'अकाउंट्स, फाइनेंस, बिज़नेस, CA और मैनेजमेंट करियर.',
      arts: 'लॉ, UPSC, कम्युनिकेशन, पॉलिसी और क्रिएटिव करियर.',
      design: 'विज़ुअल क्रिएटिविटी, एनीमेशन, UI/UX और प्रोडक्ट डिज़ाइन.',
    },
    streamHub: {
      badge: 'करियर स्ट्रीम',
      title: 'स्ट्रीम चुनें, फिर अपना रास्ता चुनें',
      subtitle: 'किसी भी stream को खोलकर देखें कि वह किस दिशा में ले जाती है, क्या पढ़ना है, कौन-सी परीक्षाएँ ज़रूरी हैं, और छात्र कॉलेज व career routes को कैसे compare करते हैं.',
      cardLink: 'पेज खोलें',
    },
    streamGuide: {
      back: 'करियर स्ट्रीम पर वापस',
      about: 'इस स्ट्रीम के बारे में',
      aboutSub: 'यह stream किसके लिए है और किस छात्र के लिए बेहतर है, इसका quick overview.',
      study: 'आप क्या पढ़ते हैं',
      studySub: '11वीं और 12वीं में बनने वाले मुख्य subjects और skills.',
      careers: 'करियर दिशा',
      careersSub: '12वीं या graduation के बाद जाने वाले मुख्य रास्ते.',
      exams: 'एंट्रेंस परीक्षाएँ',
      examsSub: 'इस stream में तैयारी की जाने वाली प्रमुख परीक्षाएँ.',
      colleges: 'शीर्ष कॉलेज और लेबल',
      collegesSub: 'Labels से institutes की तुलना जल्दी और साफ़ हो जाती है.',
      programming: 'सीखने वाले प्रोग्रामिंग लैंग्वेज',
      programmingSub: 'टेक-हैवी करियर और future skills के लिए उपयोगी.',
      tools: 'टूल्स, सॉफ्टवेयर या स्किल्स',
      toolsSub: 'ऐसी शुरुआती skills जो projects और portfolios को मजबूत बनाती हैं.',
      quickDirection: 'त्वरित दिशा',
      keyFacts: 'मुख्य तथ्य',
      nextLinks: 'अगले लिंक',
      openRoadmaps: 'stream-specific roadmaps खोलें',
      examCalendar: 'परीक्षा कैलेंडर देखें',
      counseling: 'निःशुल्क काउंसलिंग बुक करें',
      notFound: 'Stream page नहीं मिला',
    },
    hero: {
      badge: 'भारत का करियर गाइड हब, अप्रैल 2026 तक अपडेटेड',
      titleLine1: 'आपका अगला करियर फैसला',
      titleLine2: 'यहीं से शुरू होता है',
      description: 'CareerGuide आपको उलझन से स्पष्टता तक ले जाता है। 10वीं या 12वीं के बाद Engineering, Medical, Commerce, Pharmacy, Law, Design और skill-based paths के लिए step-by-step roadmap देखें.',
      point1: 'अपनी class और stream कुछ ही सेकंड में चुनें',
      point2: 'Exams, colleges और salary paths को साथ में compare करें',
      point3: 'ऐसा roadmap follow करें जो अगला कदम बताए',
      after10th: '10वीं के बाद',
      after12th: '12वीं के बाद',
      exams: 'परीक्षा कैलेंडर',
      scroll: 'और देखें',
      stats: {
        roadmaps: 'करियर रोडमैप',
        exams: 'मुख्य परीक्षाएँ',
        colleges: 'शीर्ष कॉलेज',
        latest: 'नवीनतम अपडेट',
      },
    },
    home: {
      ctaBadge: 'व्यक्तिगत करियर रोडमैप',
      ctaTitle: 'अपना Personalized Career Roadmap पाएं',
      ctaDescription: 'कुछ आसान सवालों के जवाब दें और एक practical roadmap पाएं जो सही stream, मुख्य entrance exams, college options और उस career path की salary direction दिखाए.',
      after10thTitle: '10वीं के बाद',
      after10thSub: '6 रास्ते उपलब्ध',
      after12thTitle: '12वीं के बाद',
      after12thSub: '25+ करियर रोडमैप',
      noSignup: 'साइनअप की ज़रूरत नहीं · 100% मुफ्त · अप्रैल 2026 अपडेटेड',
      howItWorks: 'यह कैसे काम करता है',
      step1Title: 'अपना स्तर चुनें',
      step1Desc: 'बताएँ आपने 10वीं या 12वीं पूरी कर ली है या नहीं',
      step2Title: 'अपनी stream चुनें',
      step2Desc: 'Science (PCM/PCB), Commerce, Arts, ITI, Polytechnic',
      step3Title: 'अपना roadmap पाएं',
      step3Desc: 'परीक्षाएँ, कॉलेज, salary और timeline के साथ step-by-step guide',
      possibilityBadge: 'आपके भविष्य के विकल्प',
      possibilityTitle: 'यह आगे किस दिशा में ले जा सकता है?',
      possibilityDesc: 'एक stream कई रास्ते खोल सकती है. आप degree, government exam, industry job, freelancing, research या startup route चुन सकते हैं.',
      possibilityOneTitle: 'पेशेवर डिग्रियाँ',
      possibilityOneDesc: 'B.Tech, MBBS, B.Com, BBA, B.Pharm, B.Des, LLB और बहुत कुछ.',
      possibilityTwoTitle: 'प्रतियोगी परीक्षाएँ',
      possibilityTwoDesc: 'JEE, NEET, CLAT, NIFT, CA, UPSC, banking और state-level entrances.',
      possibilityThreeTitle: 'जल्दी नौकरी की शुरुआत',
      possibilityThreeDesc: 'ITI, Polytechnic और skill paths 6-24 महीनों में job तक पहुँचा सकते हैं.',
      possibilityFourTitle: 'ग्रोथ और flexibility',
      possibilityFourDesc: 'Switch करें, specialize करें, freelance करें, study abroad करें या अपना business शुरू करें.',
      exampleTitle: 'उदाहरण करियर रोडमैप',
      exampleDesc: 'देखें कि छात्र 10वीं या 12वीं से career तक subjects, exams, degrees और job steps के clear sequence के साथ कैसे पहुँचते हैं।',
      popularTitle: 'लोकप्रिय करियर विकल्प',
      popularDesc: 'किसी भी career card को खोलकर जानें क्या पढ़ना है, कौन-सी परीक्षा लक्ष्य करनी है और आगे growth कैसी रहती है.',
      upcomingTitle: 'आगामी परीक्षाएँ 2026',
      upcomingDesc: 'महत्वपूर्ण exam dates, application windows और current status updates से आगे रहें.',
      salaryTitle: 'सैलरी Snapshot 2026',
      salaryDesc: 'एक सरल नज़र जहाँ earning potential शुरू होती है और experience के साथ बढ़ सकती है.',
      counselingTitle: 'अभी भी कन्फ्यूज़ हैं? Expert से बात करें!',
      counselingDesc: 'अगर streams या careers के बीच फँसे हैं, तो free 1-on-1 session बुक करें. हम marks, interests, budget और realistic next steps समझने में मदद करेंगे.',
      startQuiz: 'मुफ़्त करियर क्विज़ शुरू करें',
      bookCounseling: 'निःशुल्क काउंसलिंग बुक करें',
      viewAll: 'सभी देखें',
    },
    after10thPage: {
      badge: '10वीं के बाद',
      title: '10वीं के बाद अपना रास्ता चुनें',
      subtitle: '6 clear pathways — हर एक अलग career set तक ले जाती है. किसी भी path पर क्लिक करके details, subjects, salary और opportunities देखें.',
      tipTitle: '10वीं के छात्रों के लिए Pro Tip',
      tipBody: 'आप अभी जो path चुनते हैं, वही अगले 20 वर्षों की दिशा तय करता है. Peer pressure नहीं, बल्कि interest + aptitude के आधार पर चुनें. सभी 6 paths में excellent career opportunities हैं.',
      compareTitle: 'स्ट्रीम तुलना',
      compareSubtitle: '11वीं-12वीं के लिए कौन-सी stream सही है?',
      ctaPrompt: '12वीं पूरी कर ली? Detailed career roadmaps देखें',
      ctaButton: '12वीं के बाद के करियर देखें →',
    },
    after12thPage: {
      badge: '12वीं के बाद',
      title: 'अपनी करियर स्ट्रीम चुनें',
      subtitle: 'नीचे अपनी 12वीं stream चुनें और step-by-step roadmaps, top colleges, entrance exams व salary data देखें.',
      switchTitle: 'अच्छी खबर — Stream बदलना संभव है!',
      switchBody: 'Hotel Management, Law (CLAT), Design (NIFT/NID), Civil Services (UPSC) और कई courses सभी streams के students को स्वीकार करते हैं. Science वाले student भी Commerce/Arts careers में जा सकते हैं.',
      sectionTitle: 'सीधे किसी करियर पर जाएँ',
    },
    examCalendar: {
      badge: '📅 अप्रैल 2026 परीक्षा शेड्यूल',
      title: 'एंट्रेंस परीक्षा कैलेंडर',
      subtitle: 'Engineering, Medical, Commerce, Pharmacy, Law और Design की सभी major entrance exams की latest जानकारी रखें.',
      searchPlaceholder: 'परीक्षा नाम या stream खोजें...',
      filterAll: 'सभी',
      filterEngineering: 'इंजीनियरिंग',
      filterMedical: 'मेडिकल',
      filterCommerce: 'कॉमर्स',
      filterPharmacy: 'फार्मेसी',
      filterLaw: 'लॉ',
      filterDesign: 'डिज़ाइन',
      filterHotel: 'होटल मैनेजमेंट',
      statusActive: 'सक्रिय',
      statusUpcoming: 'आने वाली',
      statusCompleted: 'पूर्ण',
      noExams: 'कोई परीक्षा नहीं मिली. अलग search या filter आज़माएँ.',
      importantTitle: 'महत्वपूर्ण नोट',
      importantBody: 'परीक्षा की dates बदल सकती हैं. हमेशा official website पर verify करें. जल्दी registration करें और सभी documents तैयार रखें.',
      prepTitle: 'तैयारी के सुझाव',
      prepBody: 'जल्दी शुरू करें, पिछले वर्षों के papers practice करें, और official syllabus व updates follow करें.',
    },
    counselingModal: {
      title: 'निःशुल्क करियर काउंसलिंग बुक करें',
      subtitle: 'करियर expert के साथ 1-on-1 session. 100% मुफ्त.',
      stepDetails: 'आपकी जानकारी',
      stepSubject: 'करियर रुचि',
      stepTime: 'समय चुनें',
      fullName: 'पूरा नाम *',
      fullNamePlaceholder: 'अपना पूरा नाम लिखें',
      phone: 'फोन नंबर *',
      phonePlaceholder: '10 अंकों का मोबाइल नंबर',
      email: 'ईमेल (वैकल्पिक)',
      emailPlaceholder: 'your@email.com',
      currentClass: 'वर्तमान कक्षा *',
      careerInterest: 'आपको किस करियर में रुचि है? *',
      question: 'कोई विशेष प्रश्न या चिंता?',
      questionPlaceholder: 'जैसे: Engineering और Medical के बीच कन्फ्यूज़ हूँ. मेरा NEET score 550 है. क्या करूँ?',
      preferredDate: 'पसंदीदा तारीख *',
      preferredTime: 'पसंदीदा समय *',
      bookingSummary: 'बुकिंग सारांश',
      whatToExpect: 'क्या अपेक्षा करें:',
      successTitle: 'बुकिंग कन्फर्म हो गई!',
      successBodyPrefix: 'नमस्ते',
      successBodyMid: 'आपका counseling session बुक हो गया है',
      successBodyEnd: 'एक confirmation भेजा जाएगा',
      done: 'Done 🎉',
      next: 'Next →',
      back: '← Back',
      confirm: 'Booking कन्फर्म करें ✓',
      step1Label: 'आपकी जानकारी',
      step2Label: 'विषय चुनें',
      step3Label: 'समय चुनें',
    },
  },
};

const defaultLanguageContext = {
  locale: 'en',
  isHindi: false,
  setLocale: () => {},
  t: (key, fallback = '') => getValue(translations.en, key) ?? fallback,
};

const LanguageContext = createContext(defaultLanguageContext);

function getValue(source, key) {
  return key.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), source);
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      return localStorage.getItem('career-guide-language') || 'en';
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('career-guide-language', locale);
    } catch {
      // ignore storage issues
    }
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => {
    const t = (key, fallback = '') => {
      const current = getValue(translations[locale], key);
      if (current !== undefined) return current;
      const english = getValue(translations.en, key);
      if (english !== undefined) return english;
      return fallback;
    };

    return {
      locale,
      isHindi: locale === 'hi',
      setLocale: (nextLocale) => setLocaleState(nextLocale === 'hi' ? 'hi' : 'en'),
      t,
    };
  }, [locale]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
